/**
 * The coupon form's rules, apart from the form so they can be tested and so
 * the component file exports only components.
 *
 * Every rule mirrors `CouponWrite` / `CouponUpdate` and the plan-code check in
 * the backend's coupon service, so a form that passes here is one the server
 * accepts -- apart from what only the server can see (a code already taken).
 */

export const COUPON_AUDIENCES = [
  { key: 'COMPANY', label: 'Companies' },
  { key: 'STUDENT', label: 'Students' },
];

export function audienceLabel(audience) {
  return audience === 'STUDENT' ? 'Students' : 'Companies';
}

/** Students are billed in rupees; company plans default to euros. */
export function defaultCurrency(audience) {
  return audience === 'STUDENT' ? 'INR' : 'EUR';
}

export const EMPTY_COUPON = {
  audience: 'COMPANY',
  code: '',
  description: '',
  discount_type: 'PERCENTAGE',
  percent_off: '25',
  amount_off: '',
  currency: 'EUR',
  duration: 'ONCE',
  starts_at: '',
  expires_at: '',
  max_redemptions: '',
  max_uses_per_customer: '1',
  plan_codes: [],
};

/** An ISO timestamp as the value a `type="date"` input wants. */
/** A stored instant as the date it falls on here, for a date input. */
function toDateInput(value) {
  return value ? todayInput(new Date(value)) : '';
}

export function couponToForm(coupon) {
  if (!coupon) return EMPTY_COUPON;
  return {
    audience: coupon.audience ?? 'COMPANY',
    code: coupon.code,
    description: coupon.description ?? '',
    discount_type: coupon.discount_type,
    percent_off: coupon.percent_off != null ? String(coupon.percent_off) : '',
    amount_off: coupon.amount_off_cents != null ? String(coupon.amount_off_cents / 100) : '',
    currency: coupon.currency ?? defaultCurrency(coupon.audience),
    duration: coupon.duration ?? 'ONCE',
    starts_at: toDateInput(coupon.starts_at),
    expires_at: toDateInput(coupon.expires_at),
    max_redemptions: coupon.max_redemptions != null ? String(coupon.max_redemptions) : '',
    max_uses_per_customer: String(coupon.max_uses_per_customer ?? 1),
    plan_codes: coupon.plan_codes ?? [],
  };
}

/**
 * The plans a coupon for `audience` can be restricted to: that audience's
 * paid plans. A coupon on a free plan has nothing to take off, so free plans
 * are never offered; a retired plan is listed only when it is already chosen,
 * so an old restriction stays visible and removable.
 */
export function couponPlanOptions(plans, audience, selected = []) {
  return (plans ?? [])
    .filter((plan) => (plan.audience ?? 'COMPANY') === audience)
    .filter((plan) => plan.price_cents > 0)
    .filter((plan) => plan.is_active !== false || selected.includes(plan.code))
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.price_cents - b.price_cents);
}

const CODE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]*$/;
const AMOUNT_PATTERN = /^\d+(\.\d{1,2})?$/;
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** The limits in `CouponWrite` (app/schemas/coupon.py). */
export const COUPON_LIMITS = {
  codeMin: 3,
  codeMax: 40,
  descriptionMax: 2000,
  amountMax: 100000,
  redemptionsMax: 1000000,
  plansMax: 20,
};

/** Today as a `type="date"` value, in the admin's own calendar. */
export function todayInput(now = new Date()) {
  const pad = (number) => String(number).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function isDate(value) {
  return DATE_PATTERN.test(value) && !Number.isNaN(new Date(value).getTime());
}

const list = (names) => names.join(', ');

/**
 * Field errors for the form, keyed like the form; empty when it may be sent.
 *
 * `isEdit` skips what cannot change after creation; `saved` is the coupon's
 * form as stored, because a date is refused for being in the past only when it
 * is being set -- a running coupon keeps its own past start; `timesRedeemed` is
 * the floor for a new cap; `plans` is the admin catalogue, against which the
 * chosen plans are checked for audience, price and currency exactly as the
 * server does; `today` is a `YYYY-MM-DD` override for tests.
 */
export function validateCoupon(
  form,
  { isEdit = false, saved = null, timesRedeemed = 0, plans = [], today = todayInput() } = {},
) {
  const errors = {};
  const setToPast = (field) => form[field] < today && form[field] !== saved?.[field];
  const currency = form.currency.trim().toUpperCase();
  /** An unused coupon is a draft: its code, audience and kind can change. */
  const draft = !isEdit || timesRedeemed === 0;

  if (draft) {
    const code = form.code.trim();
    if (code === '') errors.code = 'Enter the code customers will type.';
    else if (code.length < COUPON_LIMITS.codeMin) {
      errors.code = 'Use at least 3 characters (up to 40).';
    } else if (code.length > COUPON_LIMITS.codeMax) {
      errors.code = 'Use 40 characters or fewer.';
    } else if (!CODE_PATTERN.test(code)) {
      errors.code =
        'Use only letters, numbers, hyphens (-) and underscores (_), starting with a letter or number. No spaces.';
    }
  }

  if (!['PERCENTAGE', 'FIXED'].includes(form.discount_type)) {
    errors.discount_type = 'Choose a percentage or a fixed amount off.';
  }
  if (!['ONCE', 'FOREVER'].includes(form.duration)) {
    errors.duration = 'Choose the first bill only or every renewal.';
  }

  if (form.description.trim().length > COUPON_LIMITS.descriptionMax) {
    errors.description = 'Keep it to 2,000 characters or fewer.';
  }

  if (form.discount_type === 'PERCENTAGE') {
    const text = String(form.percent_off).trim();
    const value = Number(text);
    if (text === '') errors.percent_off = 'Enter how much comes off, from 1 to 100.';
    else if (!Number.isInteger(value) || value < 1 || value > 100) {
      errors.percent_off = 'Enter a whole number from 1 to 100.';
    }
  } else if (form.discount_type === 'FIXED') {
    const text = String(form.amount_off).trim();
    const value = Number(text);
    const unit = /^[A-Z]{3}$/.test(currency) ? `${currency} ` : '';
    if (text === '') errors.amount_off = `Enter the amount off${unit ? ` in ${currency}` : ''}.`;
    else if (!Number.isFinite(value)) errors.amount_off = 'Enter a number, like 49.50.';
    else if (value <= 0) errors.amount_off = 'Enter an amount above 0.';
    else if (!AMOUNT_PATTERN.test(text)) errors.amount_off = 'Use at most 2 decimal places.';
    else if (value > COUPON_LIMITS.amountMax) {
      errors.amount_off = `Enter ${unit}100,000 or less.`;
    }

    if (draft && !/^[A-Z]{3}$/.test(currency)) {
      errors.currency = 'Use a 3-letter currency code, like INR or EUR.';
    }
  }

  if (form.starts_at && !isDate(form.starts_at)) errors.starts_at = 'Enter a valid date.';
  else if (form.starts_at && setToPast('starts_at')) {
    errors.starts_at = 'Pick today or a later date.';
  }
  if (form.expires_at && !isDate(form.expires_at)) errors.expires_at = 'Enter a valid date.';
  else if (form.expires_at && setToPast('expires_at')) {
    errors.expires_at = 'Pick today or a later date.';
  } else if (
    form.expires_at &&
    form.starts_at &&
    !errors.starts_at &&
    form.expires_at < form.starts_at
  ) {
    errors.expires_at = 'The expiry cannot be before the start date.';
  }

  const capText = String(form.max_redemptions).trim();
  if (capText !== '') {
    const cap = Number(capText);
    if (!Number.isInteger(cap)) {
      errors.max_redemptions = 'Use a whole number, or leave it empty for unlimited.';
    } else if (timesRedeemed > 1 && cap < timesRedeemed) {
      errors.max_redemptions = `Already redeemed ${timesRedeemed} times, so enter ${timesRedeemed} or more (or leave it empty for unlimited).`;
    } else if (cap < 1) {
      errors.max_redemptions = 'Enter 1 or more, or leave it empty for unlimited.';
    } else if (cap > COUPON_LIMITS.redemptionsMax) {
      errors.max_redemptions = 'Enter 1,000,000 or less.';
    }
  }

  const perCustomerText = String(form.max_uses_per_customer).trim();
  const perCustomer = Number(perCustomerText);
  if (perCustomerText === '') {
    errors.max_uses_per_customer = 'Enter how many times one customer may use it (1 or more).';
  } else if (!Number.isInteger(perCustomer) || perCustomer < 1) {
    errors.max_uses_per_customer = 'Enter a whole number, 1 or more.';
  } else if (perCustomer > 1000) {
    errors.max_uses_per_customer = 'Enter 1,000 or less.';
  } else if (capText !== '' && Number.isInteger(Number(capText)) && perCustomer > Number(capText)) {
    errors.max_uses_per_customer =
      'One customer cannot use it more often than the maximum redemptions in total.';
  }

  if (form.plan_codes.length > COUPON_LIMITS.plansMax) {
    errors.plan_codes = 'Choose at most 20 plans.';
  } else if (form.plan_codes.length > 0 && plans.length > 0) {
    const chosen = form.plan_codes.map((code) => ({
      code,
      plan: plans.find((row) => row.code === code),
    }));
    const word = form.audience === 'STUDENT' ? 'student' : 'company';
    const missing = chosen.filter(({ plan }) => !plan).map(({ code }) => code);
    const other = chosen
      .filter(({ plan }) => plan && (plan.audience ?? 'COMPANY') !== form.audience)
      .map(({ plan }) => plan.name);
    const free = chosen
      .filter(({ plan }) => plan && plan.price_cents === 0)
      .map(({ plan }) => plan.name);
    const mismatched =
      form.discount_type === 'FIXED'
        ? chosen
            .filter(({ plan }) => plan && plan.currency?.toUpperCase() !== currency)
            .map(({ plan }) => plan.name)
        : [];
    if (missing.length > 0) errors.plan_codes = `No such plan: ${list(missing)}.`;
    else if (other.length > 0) {
      errors.plan_codes = `Not ${word} plans: ${list(other)}. Remove them, or change who the coupon is for.`;
    } else if (free.length > 0) {
      errors.plan_codes = `Free plans cannot take a discount: ${list(free)}.`;
    } else if (mismatched.length > 0) {
      errors.plan_codes = `Not billed in ${currency}: ${list(mismatched)}.`;
    }
  }

  return errors;
}

const toIso = (date) => new Date(date).toISOString();
/** An expiry date means the coupon works through that whole day (in the
 * admin's time zone), so "expires today" still works until midnight. */
const toExpiryIso = (date) => new Date(`${date}T23:59:59.999`).toISOString();

/** The create request. Exactly one of the two discount fields is sent. */
export function couponCreateBody(form) {
  const percentage = form.discount_type === 'PERCENTAGE';
  return {
    code: form.code.trim().toUpperCase(),
    audience: form.audience,
    description: form.description.trim() || undefined,
    discount_type: form.discount_type,
    percent_off: percentage ? Number(form.percent_off) : undefined,
    amount_off_cents: percentage ? undefined : Math.round(Number(form.amount_off) * 100),
    currency: percentage ? undefined : form.currency.trim().toUpperCase(),
    duration: form.duration,
    starts_at: form.starts_at ? toIso(form.starts_at) : undefined,
    expires_at: form.expires_at ? toExpiryIso(form.expires_at) : undefined,
    max_redemptions: form.max_redemptions.trim() ? Number(form.max_redemptions) : undefined,
    max_uses_per_customer: Number(form.max_uses_per_customer) || 1,
    plan_codes: form.plan_codes.length > 0 ? form.plan_codes : undefined,
  };
}

const samePlans = (a, b) => a.length === b.length && a.every((code) => b.includes(code));

/**
 * The PATCH body: only what changed. An emptied optional field is sent as its
 * `clear_*` flag, because a PATCH cannot otherwise tell "remove the limit"
 * from "leave it".
 */
export function couponChanges(coupon, form) {
  const before = couponToForm(coupon);
  const body = {};
  const changed = (key) => String(form[key]).trim() !== String(before[key]).trim();

  const kindChanged = form.discount_type !== coupon.discount_type;
  if (form.code.trim().toUpperCase() !== String(coupon.code).toUpperCase()) {
    body.code = form.code.trim().toUpperCase();
  }
  if (form.audience !== coupon.audience) body.audience = form.audience;
  if (kindChanged) body.discount_type = form.discount_type;
  if (changed('description')) body.description = form.description.trim() || null;
  if (form.discount_type === 'PERCENTAGE' && (kindChanged || changed('percent_off'))) {
    body.percent_off = Number(form.percent_off);
  }
  if (form.discount_type === 'FIXED' && (kindChanged || changed('amount_off'))) {
    body.amount_off_cents = Math.round(Number(form.amount_off) * 100);
  }
  if (
    form.discount_type === 'FIXED' &&
    form.currency.trim().toUpperCase() !== String(coupon.currency ?? '').toUpperCase()
  ) {
    body.currency = form.currency.trim().toUpperCase();
  }
  if (form.duration !== before.duration) body.duration = form.duration;
  if (changed('starts_at') && form.starts_at) body.starts_at = toIso(form.starts_at);
  if (changed('expires_at')) {
    if (form.expires_at) body.expires_at = toExpiryIso(form.expires_at);
    else body.clear_expiry = true;
  }
  if (changed('max_uses_per_customer')) {
    body.max_uses_per_customer = Number(form.max_uses_per_customer);
  }
  if (changed('max_redemptions')) {
    if (form.max_redemptions.trim()) body.max_redemptions = Number(form.max_redemptions);
    else body.clear_max_redemptions = true;
  }
  if (!samePlans(form.plan_codes, before.plan_codes)) {
    if (form.plan_codes.length > 0) body.plan_codes = form.plan_codes;
    else body.clear_plan_restriction = true;
  }
  return body;
}

/** Server field names that the form calls something else. */
export const COUPON_SERVER_FIELDS = { amount_off_cents: 'amount_off' };
